export type PscAnswer = 0 | 1 | 2;

export type LikertAnswer = 1 | 2 | 3 | 4 | 5;

export type SubscaleKey = "attention" | "emotional" | "behavior";

export type SubscaleState = "Doing well" | "Elevated" | "Needs attention";

export type OverallBand = "blue" | "yellow" | "red";

export type CaregiverIndicator = "Doing well" | "Needs attention";

export type PscOption = {
  readonly label: "Never" | "Sometimes" | "Often";
  readonly value: PscAnswer;
};

export type LikertOption = {
  readonly label: "Strongly disagree" | "Disagree" | "Neutral" | "Agree" | "Strongly agree";
  readonly value: LikertAnswer;
};

export type PscItem = {
  readonly id: string;
  readonly subscale: SubscaleKey;
  readonly text: string;
};

export type CaregiverItem = {
  readonly id: string;
  readonly text: string;
};

export type SubscaleResult = {
  readonly key: SubscaleKey;
  readonly label: string;
  readonly score: number;
  readonly state: SubscaleState;
};

export type ScreenerResult = {
  readonly total: number;
  readonly band: OverallBand;
  readonly headline: string;
  readonly body: string;
  readonly subscales: readonly SubscaleResult[];
  readonly caregiverIndicator: CaregiverIndicator;
};

export const PSC_OPTIONS: readonly PscOption[] = [
  { label: "Never", value: 0 },
  { label: "Sometimes", value: 1 },
  { label: "Often", value: 2 }
];

export const LIKERT_OPTIONS: readonly LikertOption[] = [
  { label: "Strongly disagree", value: 1 },
  { label: "Disagree", value: 2 },
  { label: "Neutral", value: 3 },
  { label: "Agree", value: 4 },
  { label: "Strongly agree", value: 5 }
];

export const SUBSCALE_LABELS: Readonly<Record<SubscaleKey, string>> = {
  attention: "Attention & focus",
  emotional: "Emotional wellbeing",
  behavior: "Behavior & conduct"
};

export const ATTENTION_ITEMS: readonly PscItem[] = [
  { id: "attention-1", subscale: "attention", text: "Had trouble concentrating or paying attention" },
  { id: "attention-2", subscale: "attention", text: "Been easily distracted" },
  { id: "attention-3", subscale: "attention", text: "Had difficulty finishing tasks" },
  { id: "attention-4", subscale: "attention", text: "Acted as if driven by a motor" },
  { id: "attention-5", subscale: "attention", text: "Fidgeted or was unable to sit still" }
];

export const EMOTIONAL_ITEMS: readonly PscItem[] = [
  { id: "emotional-1", subscale: "emotional", text: "Felt sad, unhappy, or hopeless" },
  { id: "emotional-2", subscale: "emotional", text: "Seemed to be having less fun" },
  { id: "emotional-3", subscale: "emotional", text: "Felt worried or fearful" },
  { id: "emotional-4", subscale: "emotional", text: "Felt nervous, high-strung, or tense" },
  { id: "emotional-5", subscale: "emotional", text: "Seemed down on him or herself" }
];

export const BEHAVIOR_ITEMS: readonly PscItem[] = [
  { id: "behavior-1", subscale: "behavior", text: "Fought with other children" },
  { id: "behavior-2", subscale: "behavior", text: "Did not listen to rules" },
  { id: "behavior-3", subscale: "behavior", text: "Did not understand other people's feelings" },
  { id: "behavior-4", subscale: "behavior", text: "Teased others" },
  { id: "behavior-5", subscale: "behavior", text: "Blamed others for his or her troubles" },
  { id: "behavior-6", subscale: "behavior", text: "Refused to share" }
];

export const PSC_ITEMS: readonly PscItem[] = [...ATTENTION_ITEMS, ...EMOTIONAL_ITEMS, ...BEHAVIOR_ITEMS];

export const PSC_TOTAL_MAX = PSC_ITEMS.length * 2;

export const CAREGIVER_ITEMS: readonly CaregiverItem[] = [
  { id: "caregiver-1", text: "I am able to enjoy the positive moments with my child" },
  { id: "caregiver-2", text: "I am confident in managing stress related to my child's needs" },
  { id: "caregiver-3", text: "I feel emotionally capable of managing my parenting responsibilities" },
  { id: "caregiver-4", text: "I have the physical energy for the demands of parenting" }
];

export type PscAnswers = Readonly<Record<string, PscAnswer>>;

export type CaregiverAnswers = Readonly<Record<string, LikertAnswer>>;

const SUBSCALE_ELEVATED_THRESHOLD = 5;
const SUBSCALE_ATTENTION_THRESHOLD = 7;
const OVERALL_YELLOW_THRESHOLD = 11;
const OVERALL_RED_THRESHOLD = 15;
const CAREGIVER_DISAGREE_CEILING = 2;
const CAREGIVER_NEEDS_ATTENTION_COUNT = 2;

export function subscaleState(score: number): SubscaleState {
  if (score >= SUBSCALE_ATTENTION_THRESHOLD) return "Needs attention";
  if (score >= SUBSCALE_ELEVATED_THRESHOLD) return "Elevated";
  return "Doing well";
}

const OVERALL_BANDS: Readonly<Record<OverallBand, { headline: string; body: string }>> = {
  blue: {
    headline: "Your child is doing well",
    body: "Keep doing what you're doing! If anything changes or you have questions, Manatee is here for your family."
  },
  yellow: {
    headline: "Some areas worth watching",
    body: "Your child's score suggests some areas to keep an eye on; nothing alarming, but if you want a professional opinion, you can connect with the care team at Manatee to get clarity."
  },
  red: {
    headline: "Some areas need attention",
    body: "Your child's score is higher than we'd like and warrants a closer look. The good news is: you caught it early! Manatee can connect you with a therapist to make a plan this week."
  }
};

export function overallBand(total: number): OverallBand {
  if (total >= OVERALL_RED_THRESHOLD) return "red";
  if (total >= OVERALL_YELLOW_THRESHOLD) return "yellow";
  return "blue";
}

function sumSubscale(items: readonly PscItem[], answers: PscAnswers): number {
  return items.reduce((total, item) => total + (answers[item.id] ?? 0), 0);
}

export function caregiverIndicator(answers: CaregiverAnswers): CaregiverIndicator {
  const disagreeCount = CAREGIVER_ITEMS.reduce((count, item) => {
    const answer = answers[item.id];
    return answer && answer <= CAREGIVER_DISAGREE_CEILING ? count + 1 : count;
  }, 0);
  return disagreeCount >= CAREGIVER_NEEDS_ATTENTION_COUNT ? "Needs attention" : "Doing well";
}

export function scoreScreener(pscAnswers: PscAnswers, caregiverAnswers: CaregiverAnswers): ScreenerResult {
  const subscales: SubscaleResult[] = (Object.keys(SUBSCALE_LABELS) as SubscaleKey[]).map(key => {
    const items = PSC_ITEMS.filter(item => item.subscale === key);
    const score = sumSubscale(items, pscAnswers);
    return { key, label: SUBSCALE_LABELS[key], score, state: subscaleState(score) };
  });

  const total = subscales.reduce((sum, subscale) => sum + subscale.score, 0);
  const band = overallBand(total);

  return {
    total,
    band,
    headline: OVERALL_BANDS[band].headline,
    body: OVERALL_BANDS[band].body,
    subscales,
    caregiverIndicator: caregiverIndicator(caregiverAnswers)
  };
}
